import { Link, useLocation } from 'react-router-dom';
import { Folder, FileText, Inbox, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import { useSections } from '../hooks/useSections';
import { useChapterStatus } from '../hooks/useChapterStatus';
import { useLanguage } from '../hooks/useLanguage';

export default function Sidebar() {
  const { data, isLoading } = useSections();
  const location = useLocation();
  const { lang } = useLanguage();

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-500">Načítám…</div>;
  }

  const sections = data?.sections ?? [];

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 text-center text-gray-500">
        <Inbox className="h-8 w-8 text-gray-300" />
        <div className="text-xs">Žádné sekce</div>
      </div>
    );
  }

  return (
    <nav className="py-4">
      {sections.map((s) => {
        const href = `/s/${s.slug}`;
        const active = location.pathname === href || location.pathname.startsWith(`/c/${s.slug}/`);
        return (
          <div key={s.id} className="mb-1">
            <Link
              to={href}
              className={clsx(
                'mx-2 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active ? 'bg-teal-light text-teal' : 'text-gray-700 hover:bg-white hover:text-dark',
              )}
            >
              <Folder className="h-4 w-4 shrink-0" />
              <span className="truncate">{(lang === 'en' && s.titleEn) || s.titleCs}</span>
            </Link>
            {active && <ChapterTree sectionId={s.id} sectionSlug={s.slug} />}
          </div>
        );
      })}
    </nav>
  );
}

function ChapterTree({ sectionId, sectionSlug }: { sectionId: string; sectionSlug: string }) {
  const { data } = useSectionChaptersInner(sectionId);
  const { data: statusData } = useChapterStatus();
  const location = useLocation();
  const { lang } = useLanguage();
  if (!data) return null;

  const titleOf = (ch: ChapterListItem): string => {
    if (lang === 'en') return ch.publishedTitleEn || ch.draftTitleEn || ch.draftTitleCs;
    return ch.draftTitleCs;
  };

  const statusMap = new Map((statusData?.statuses ?? []).map((s) => [s.chapterId, s]));
  const topLevel = data.filter((ch) => !ch.parentChapterId);

  return (
    <div className="ml-4 mt-1 space-y-0.5 border-l border-gray-200 pl-2">
      {topLevel.map((ch) => {
        const href = `/c/${sectionSlug}/${ch.slug}`;
        const active = location.pathname.startsWith(href);
        const subs = data.filter((c) => c.parentChapterId === ch.id);
        const st = statusMap.get(ch.id);
        return (
          <div key={ch.id}>
            <Link
              to={href}
              className={clsx(
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                active ? 'bg-white text-teal font-semibold' : 'text-gray-600 hover:text-dark',
              )}
            >
              <FileText className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">{titleOf(ch)}</span>
              <StatusIndicator status={st} />
            </Link>
            {active && subs.length > 0 && (
              <div className="ml-4 space-y-0.5 border-l border-gray-200 pl-2">
                {subs.map((sub) => {
                  const subHref = `/c/${sectionSlug}/${ch.slug}/${sub.slug}`;
                  const subActive = location.pathname === subHref;
                  const subSt = statusMap.get(sub.id);
                  return (
                    <Link
                      key={sub.id}
                      to={subHref}
                      className={clsx(
                        'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs transition-colors',
                        subActive ? 'text-teal font-semibold' : 'text-gray-500 hover:text-dark',
                      )}
                    >
                      <span className="flex-1 truncate">{titleOf(sub)}</span>
                      <StatusIndicator status={subSt} />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusIndicator({ status }: { status?: { status: string; ackRequired: boolean; ackDone: boolean } }) {
  if (!status) return null;
  // Vyžaduje ack, ještě neodsouhlaseno, máme aktuální verzi → gold ShieldAlert
  if (status.ackRequired && !status.ackDone && status.status === 'current') {
    return <ShieldAlert className="h-3 w-3 shrink-0 text-gold" aria-label="Vyžaduje potvrzení" />;
  }
  // Nová od poslední návštěvy
  if (status.status === 'unread' || status.status === 'stale') {
    return (
      <span
        className="h-2 w-2 shrink-0 rounded-full bg-teal"
        title={status.status === 'unread' ? 'Zatím nečteno' : 'Aktualizováno od poslední návštěvy'}
      />
    );
  }
  return null;
}

// Interní helper používající react-query (separátní soubor by byl overkill).
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ChapterListItem } from '@shared/types';

function useSectionChaptersInner(sectionId: string) {
  return useQuery({
    queryKey: ['sections', sectionId, 'chapters-sidebar'],
    queryFn: () =>
      api
        .get<{ chapters: ChapterListItem[] }>(`/sections/${sectionId}/chapters`)
        .then((r) => r.chapters),
  });
}
